import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNotification } from './useNotification';
import { useFetchApi } from './useFetchApi';
import { User } from '../shared/types';
import { getPropertiesDiff } from '../helper/data-transform';

export type SupplierRiskAssessmentStatus =
  | 'NEW'
  | 'UNDER_REVIEW'
  | 'READY_FOR_ASSESSMENT'
  | 'CMS_ASSESSMENT_REQUESTED'
  | 'CMS_ASSESSMENT_COMPLETED'
  | 'IN_RISK_MITIGATION'
  | 'EUDR_COMPLIANT'
  | 'EUDR_NON_COMPLIANT';

export type SupplierRiskAssessmentResponse = {
  id?: string;
  supplierId?: string;
  buyerId?: string;
  updatedAt?: string;
  status: SupplierRiskAssessmentStatus;
  notes: string;
};

export type SupplierRiskAssessmentMutation = UseMutationResult<
  SupplierRiskAssessmentResponse,
  unknown,
  SupplierRiskAssessmentResponse,
  unknown
>;

type UseSupplierRiskAssessmentsResult = {
  data: SupplierRiskAssessmentResponse[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  createAssessment: SupplierRiskAssessmentMutation;
  updateAssessment: SupplierRiskAssessmentMutation;
};

export type SupplierRiskAssessmentHistory = {
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  afterData: SupplierRiskAssessmentResponse;
  beforeData: SupplierRiskAssessmentResponse;
  changedAt: string;
  changedByUser: User;
  changedByUserType: 'USER' | 'SYSTEM' | 'M2M';
  status?: SupplierRiskAssessmentStatus;
  notes?: string;
  updatedAt: string;
  updatedBy?: string;
};

export const SUPPLIER_RISK_QUERY = 'supplier-risk';
export const useGetSupplierRiskAssessments = () => {
  const fetch = useFetchApi();
  const { apiError } = useNotification();

  return useQuery({
    queryKey: [SUPPLIER_RISK_QUERY],
    queryFn: () => fetch('supplier-risk-assessments'),
    onError: apiError,
    refetchOnWindowFocus: false,
  });
};

export const SUPPLIER_RISK_HISTORY_QUERY = 'supplier-risk-history';
export const useGetSupplierRiskHistory = (assessmentId: string | undefined) => {
  const fetch = useFetchApi();
  const { apiError } = useNotification();

  return useQuery({
    queryKey: [SUPPLIER_RISK_HISTORY_QUERY, assessmentId],
    queryFn: () => fetch(`supplier-risk-assessments/${assessmentId}/history`),
    enabled: !!assessmentId,
    onError: apiError,
    select: (data: SupplierRiskAssessmentHistory[]) => {
      return data.map((record) => ({
        ...record,
        ...getPropertiesDiff({
          before: record.beforeData,
          after: record.afterData,
        }),
        updatedBy: [
          record.changedByUser.firstName,
          record.changedByUser.lastName,
        ].join(' '),
      }));
    },
  });
};

export const useSupplierRiskAssessments =
  (): UseSupplierRiskAssessmentsResult => {
    const fetch = useFetchApi();
    const { showErrorWithResponse, successfullyUpdated } = useNotification();
    const queryClient = useQueryClient();

    const { data, isLoading, isFetching } = useGetSupplierRiskAssessments();

    const onSuccess = (data: SupplierRiskAssessmentResponse) => {
      queryClient.invalidateQueries({
        queryKey: [SUPPLIER_RISK_HISTORY_QUERY, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [SUPPLIER_RISK_QUERY],
      });
      successfullyUpdated('Supplier status');
    };

    const createAssessment = useMutation({
      mutationFn: (data: SupplierRiskAssessmentResponse) => {
        const url = `businesses/${data.supplierId}/supplier-risk-assessments`;
        return fetch(url, {
          method: 'POST',
          body: JSON.stringify({
            status: data.status,
            notes: data.notes,
          }),
        });
      },
      onSuccess,
      onError: showErrorWithResponse,
    });

    const updateAssessment = useMutation({
      mutationFn: (data: SupplierRiskAssessmentResponse) => {
        const url = `supplier-risk-assessments/${data.id}`;
        return fetch(url, {
          method: 'PATCH',
          body: JSON.stringify({
            status: data.status,
            notes: data.notes,
          }),
        });
      },
      onSuccess,
      onError: showErrorWithResponse,
    });

    return {
      data,
      isLoading,
      isFetching,
      createAssessment,
      updateAssessment,
    };
  };
